import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ClipboardCheck, Check, X, Clock, MapPin, User, CalendarDays } from "lucide-react";

export default function ApprovalsPage() {
  const { authAxios, isAdmin, isSubAdmin, isTeamLead, canApprove } = useAuth();
  const { refresh: refreshBadges } = useNotifications();
  const navigate = useNavigate();
  const [pendingEvents, setPendingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPending = useCallback(async () => {
    try {
      const api = authAxios();
      const res = await api.get("/events/pending");
      setPendingEvents(res.data);
    } catch (err) {
      if (err.response?.status === 403) toast.error("Access denied");
      else toast.error("Failed to load pending events");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleApprove = async (eventId) => {
    try {
      const api = authAxios();
      await api.put(`/events/${eventId}/approve`);
      toast.success("Event approved!");
      fetchPending();
      refreshBadges();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve");
    }
  };

  const handleReject = async (eventId) => {
    try {
      const api = authAxios();
      await api.put(`/events/${eventId}/reject`);
      toast.success("Event rejected");
      fetchPending();
      refreshBadges();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject");
    }
  };

  if (!isAdmin && !isSubAdmin && !isTeamLead) {
    return (
      <div className="p-6 md:p-8 lg:p-12 w-full text-center py-16">
        <ClipboardCheck className="h-16 w-16 mx-auto text-muted-foreground/20 mb-4" />
        <h2 className="font-heading text-2xl font-bold">Access Denied</h2>
        <p className="text-muted-foreground mt-2">Only Admin and Team Leads can view approvals.</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-12 w-full space-y-8" data-testid="approvals-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-foreground flex items-center gap-3">
          <ClipboardCheck className="h-8 w-8 text-primary" />
          Event Approvals
        </h1>
        <p className="text-muted-foreground mt-1">Review and approve pending event requests</p>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />)}
        </div>
      ) : pendingEvents.length > 0 ? (
        <div className="space-y-4" data-testid="pending-events-list">
          {pendingEvents.map((event) => (
            <Card key={event.event_id} className="border-border/50 shadow-sm border-l-4 border-l-amber-500" data-testid={`pending-event-${event.event_id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <h3 className="font-heading text-lg font-semibold text-foreground">{event.title}</h3>
                      <Badge className="bg-amber-500/10 text-amber-600">
                        <Clock className="h-3 w-3 mr-1" /> Pending
                      </Badge>
                    </div>
                    {event.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{event.description}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <CalendarDays className="h-3.5 w-3.5" />
                        {event.start_date} - {event.end_date}
                      </span>
                      {event.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" /> {event.location}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <User className="h-3.5 w-3.5" />
                        Requested by: {event.creator?.name || event.creator_name || "Unknown"}
                      </span>
                    </div>
                    {event.days?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {event.days.map((d) => (
                          <span key={d.day_number} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-md font-medium">
                            {d.day_name?.slice(0, 3)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => navigate(`/events/${event.event_id}`)}
                      data-testid={`view-pending-${event.event_id}`}
                    >
                      View Details
                    </Button>
                    {canApprove && (
                      <>
                        <Button
                          size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => handleApprove(event.event_id)}
                          data-testid={`approve-event-${event.event_id}`}
                        >
                          <Check className="h-4 w-4 mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm" variant="destructive"
                          onClick={() => handleReject(event.event_id)}
                          data-testid={`reject-event-${event.event_id}`}
                        >
                          <X className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16" data-testid="no-pending-events">
          <ClipboardCheck className="h-16 w-16 mx-auto text-muted-foreground/20 mb-4" />
          <h3 className="text-lg font-heading font-semibold text-foreground">No Pending Events</h3>
          <p className="text-muted-foreground mt-1">All event requests have been processed</p>
        </div>
      )}
    </div>
  );
}
